import { addDays, format, parseISO, startOfWeek } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  type BlockedSlotWithStaff,
  type Clinic,
  type ClinicId,
  formatName,
  getRotationWeek,
  REGIONS_BY_ID,
  type ScheduleBlock,
  type ScheduleOverrideWithRelations,
  toWallClockDate,
  wallClockInterval,
} from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from '@/frontend/components/ui/popover'
import { useListBlockedSlots } from '@/frontend/features/appointments/use-list-blocked-slots'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useClinicSchedules } from '@/frontend/features/schedules/use-clinic-schedules'
import { useListScheduleOverrides } from '@/frontend/features/schedules/use-list-schedule-overrides'
import { cn } from '@/frontend/lib/utils'

const WEEKS_SHOWN = 4

type CoverageVet = {
  name: string
  templateName?: string
  /** Off for the whole shift, so not counted as working. */
  blocked: boolean
  /** The one-off shift that puts this vet here. */
  override?: ScheduleOverrideWithRelations
}

/**
 * True when a single blocked slot for `email` fully covers the shift's working
 * window on `date` — i.e. the vet is off for the whole shift. The block's clinic is
 * ignored: a vet blocked all day is off regardless of which clinic it names.
 */
function shiftBlockedAllDay(
  blockedSlots: BlockedSlotWithStaff[],
  email: string,
  date: string,
  blocks: ScheduleBlock[],
  timeZone: string
): boolean {
  if (blocks.length === 0) return false
  const intervals = blocks.map((b) =>
    wallClockInterval({ date, time: b.startTime }, { date, time: b.endTime }, timeZone)
  )
  const windowStart = Math.min(...intervals.map((i) => i.start.getTime()))
  const windowEnd = Math.max(...intervals.map((i) => i.end.getTime()))
  return blockedSlots.some(
    (b) =>
      b.staff.email === email &&
      new Date(b.startTime).getTime() <= windowStart &&
      new Date(b.endTime).getTime() >= windowEnd
  )
}

/** Shifts a YYYY-MM-DD calendar date by whole days. */
function shiftDate(date: string, days: number): string {
  return format(addDays(parseISO(date), days), 'yyyy-MM-dd')
}

/**
 * Clinic × day grid of how many vets are working, over four weeks. Days with no
 * vet are flagged so gaps in cover stand out. Click a day to see who's working
 * and to add or edit its one-off shifts.
 */
export function ClinicCoverageGrid({
  clinics,
  onAdd,
  onEdit,
}: {
  clinics: Clinic[]
  onAdd: (cell: { clinicId: ClinicId; date: string }) => void
  onEdit: (override: ScheduleOverrideWithRelations) => void
}) {
  const { region } = useViewerRegion()
  const timeZone = REGIONS_BY_ID[region].timezone
  const today = toWallClockDate(new Date(), timeZone)
  const thisMonday = format(startOfWeek(parseISO(today), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  const [weekStart, setWeekStart] = useState(thisMonday)

  const from = weekStart
  const to = shiftDate(weekStart, WEEKS_SHOWN * 7)
  const dates = Array.from({ length: WEEKS_SHOWN * 7 }, (_, i) => shiftDate(weekStart, i))

  // Clinics closed before the range starts have nothing to show.
  const visibleClinics = clinics.filter((c) => !c.archivedAt || c.archivedAt > from)

  const clinicIds = visibleClinics.map((c) => c.id)
  const schedulesQuery = useClinicSchedules({ clinicIds, from, to })
  const blockedQuery = useListBlockedSlots({ from, to })
  const overridesQuery = useListScheduleOverrides({ from, to, clinicIds })
  // Counts are only trustworthy once both land: a missing blocked slot would
  // count an absent vet as working and hide a real gap.
  const isLoading = schedulesQuery.isLoading || blockedQuery.isLoading
  const isError = schedulesQuery.isError || blockedQuery.isError
  const { schedules } = schedulesQuery
  const blockedSlots = blockedQuery.data
  const overrides = overridesQuery.data

  const vetsByClinicDate = useMemo(() => {
    const map = new Map<string, CoverageVet[]>()
    const overridesByCell = new Map<string, ScheduleOverrideWithRelations[]>()
    for (const o of overrides ?? []) {
      const key = `${o.clinicId}:${o.date}`
      overridesByCell.set(key, [...(overridesByCell.get(key) ?? []), o])
    }
    for (const { clinicId, days } of schedules) {
      const clinicTimeZone = clinics.find((c) => c.id === clinicId)?.timezone ?? timeZone
      for (const day of days) {
        const key = `${clinicId}:${day.date}`
        const cellOverrides = overridesByCell.get(key) ?? []
        const vets: CoverageVet[] = day.vets.map((v) => ({
          name: formatName(v.staff),
          templateName: v.templateName,
          blocked: shiftBlockedAllDay(
            blockedSlots ?? [],
            v.staff.email,
            day.date,
            v.schedule,
            clinicTimeZone
          ),
          override: cellOverrides.find((o) => o.staffId === v.staff.id),
        }))
        map.set(key, vets)
      }
    }
    return map
  }, [schedules, blockedSlots, overrides, clinics, timeZone])

  const uncoveredUpcoming =
    isLoading || isError
      ? 0
      : visibleClinics.reduce(
          (total, clinic) =>
            total +
            dates.filter(
              (date) =>
                date >= today &&
                !(clinic.archivedAt && date >= clinic.archivedAt) &&
                !(vetsByClinicDate.get(`${clinic.id}:${date}`) ?? []).some((v) => !v.blocked)
            ).length,
          0
        )

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setWeekStart(thisMonday)}>
          This week
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Previous week"
          onClick={() => setWeekStart(shiftDate(weekStart, -7))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Next week"
          onClick={() => setWeekStart(shiftDate(weekStart, 7))}
        >
          <ChevronRight className="size-4" />
        </Button>
        <span className="text-sm font-medium">
          {format(parseISO(from), 'd MMM')} – {format(parseISO(shiftDate(to, -1)), 'd MMM yyyy')}
        </span>
        {uncoveredUpcoming > 0 && (
          <span className="ml-auto rounded-md bg-destructive px-2 py-1 text-sm font-medium text-destructive-fg">
            {uncoveredUpcoming} upcoming clinic {uncoveredUpcoming === 1 ? 'day' : 'days'} without a
            vet
          </span>
        )}
      </div>

      {isError && (
        <p className="mb-2 text-sm text-destructive-fg">Some clinic schedules failed to load.</p>
      )}

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-background" />
              {Array.from({ length: WEEKS_SHOWN }, (_, w) => {
                const monday = dates[w * 7]
                return (
                  <th
                    key={monday}
                    colSpan={7}
                    className="border-l px-2 pt-2 text-xs font-semibold text-muted-foreground"
                  >
                    {format(parseISO(monday), 'd MMM')} · Rota week{' '}
                    {getRotationWeek(new Date(`${monday}T12:00:00Z`), timeZone)}
                  </th>
                )
              })}
            </tr>
            <tr>
              <th className="sticky left-0 bg-background px-3 py-1 text-left font-medium">
                Clinic
              </th>
              {dates.map((date, i) => (
                <th
                  key={date}
                  className={cn(
                    'min-w-9 px-1 py-1 text-center text-xs font-normal text-muted-foreground',
                    i % 7 === 0 && 'border-l',
                    date === today && 'font-semibold text-foreground'
                  )}
                >
                  <div>{format(parseISO(date), 'EEEEE')}</div>
                  <div>{format(parseISO(date), 'd')}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleClinics.map((clinic) => (
              <tr key={clinic.id} className="border-t">
                <th className="sticky left-0 whitespace-nowrap bg-background px-3 py-1.5 text-left font-medium">
                  {clinic.name}
                </th>
                {dates.map((date, i) => {
                  const closed = !!clinic.archivedAt && date >= clinic.archivedAt
                  const vets = vetsByClinicDate.get(`${clinic.id}:${date}`) ?? []
                  const working = vets.filter((v) => !v.blocked).length
                  return (
                    <td
                      key={date}
                      className={cn(
                        'p-0.5 text-center',
                        i % 7 === 0 && 'border-l',
                        i % 7 >= 5 && 'bg-muted/50',
                        date === today && 'bg-accent'
                      )}
                    >
                      {closed ? null : (
                        <CoverageCell
                          title={`${clinic.name} · ${format(parseISO(date), 'EEE d MMM')}`}
                          vets={vets}
                          working={working}
                          status={isLoading ? 'loading' : isError ? 'error' : 'ready'}
                          past={date < today}
                          onAdd={() => onAdd({ clinicId: clinic.id, date })}
                          onEdit={onEdit}
                        />
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * A day's vet count, red when nobody is working and dotted when a one-off shift
 * is involved. Opens a popover listing the vets with add/edit actions.
 */
function CoverageCell({
  title,
  vets,
  working,
  status,
  past,
  onAdd,
  onEdit,
}: {
  title: string
  vets: CoverageVet[]
  working: number
  status: 'loading' | 'error' | 'ready'
  past: boolean
  onAdd: () => void
  onEdit: (override: ScheduleOverrideWithRelations) => void
}) {
  const hasOneOff = vets.some((v) => v.override)

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            className={cn(
              'relative h-8 w-full rounded-sm text-sm tabular-nums hover:ring-1 hover:ring-border',
              status === 'ready' &&
                working === 0 &&
                'bg-destructive font-semibold text-destructive-fg',
              past && 'opacity-50'
            )}
          />
        }
      >
        {status === 'loading' ? '·' : status === 'error' ? '–' : working}
        {hasOneOff && (
          <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-info-fg" />
        )}
      </PopoverTrigger>
      <PopoverContent align="start">
        <PopoverTitle className="text-sm">{title}</PopoverTitle>
        {vets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No vets working.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {vets.map((v) => (
              <li key={v.override?.id ?? v.name} className="flex items-center gap-2 text-sm">
                <span className={cn('flex-1 truncate', v.blocked && 'line-through opacity-60')}>
                  {v.name}
                </span>
                {v.override ? (
                  <Badge variant="info" size="sm">
                    One-off · {v.override.template.name}
                  </Badge>
                ) : (
                  <Badge variant="neutral" size="sm">
                    {v.templateName ? `Rota · ${v.templateName}` : 'Rota'}
                  </Badge>
                )}
                {v.blocked && !v.override && (
                  <Badge variant="neutral" size="sm">
                    Off all day
                  </Badge>
                )}
                {v.override && (
                  <PopoverClose
                    render={<Button variant="outline" size="sm" />}
                    onClick={() => v.override && onEdit(v.override)}
                  >
                    Edit
                  </PopoverClose>
                )}
              </li>
            ))}
          </ul>
        )}
        <PopoverClose render={<Button size="sm" />} onClick={onAdd}>
          Add one-off shift
        </PopoverClose>
      </PopoverContent>
    </Popover>
  )
}
